import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../components/Header";
import ModalConfirmLogout from "../../components/ModalConfirmLogout";
import SocialMediaIcons from "../../components/SocialMediaIcons";
import { ApiService, VideoData } from "../../services/apiService";

const VideoPlayer: React.FC = () => {
  const [isMobile, setIsMobile] = useState(false);
  const [video, setVideo] = useState<VideoData | null>(null);
  const [loading, setLoading] = useState(true);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const navigate = useNavigate();
  const { videoId } = useParams();

  useEffect(() => {
    const checkMobile = () => {
      const isMobileDevice = window.innerWidth <= 768 || /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
      setIsMobile(isMobileDevice);
    };
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Load video data
  useEffect(() => {
    const loadVideo = async () => {
      if (!videoId) return;
      
      try {
        setLoading(true);
        const videoData = await ApiService.getVideoById(videoId);
        
        if (videoData) {
          
          // Check if videoUrl is valid
          if (!videoData.videoUrl) {
            alert('Video URL không tồn tại!');
            return;
          }
          
        } else {
          alert('Không tìm thấy video!');
        }
        
        setVideo(videoData);
      } catch (error) {
        console.error('Error loading video:', error);
      } finally {
        setLoading(false);
      }
    };

    loadVideo();
  }, [videoId]);

  const handleBack = () => {
    navigate("/tong-hop-video");
  };

  if (loading) {
    return (
      <div className="min-h-screen relative flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  if (!video) {
    return (
      <div className="min-h-screen relative flex items-center justify-center">
        <div className="text-white text-xl">Video không tồn tại</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen relative">
      <Header setIsShowLogout={() => setShowLogoutModal(true)} />
      
      {/* Background Image */}
      <div 
        className="fixed inset-0 w-full h-full -z-10"
        style={{
          backgroundImage: `url(${isMobile ? "/assets/bg_mobile_2.jpg" : "/assets/bg_pc_2.jpg"})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat'
        }}
      ></div>

      {/* Header Section */}
      <div className="px-6 py-2">
        <div className="flex items-center justify-between max-w-6xl mx-auto">
          {/* Logo - Hidden on mobile */}
          <img 
            src="/assets/logo-2.png" 
            alt="logo" 
            className="h-16 hidden sm:block"
          />
          
          {/* Back Button - Smaller on mobile */}
          <button
            onClick={handleBack}
            className="group relative flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 rounded-full font-bold text-[#DF3125] transition-all duration-500 hover:scale-110 hover:rotate-1 overflow-hidden
                       text-sm sm:text-lg"
            style={{
              background: 'linear-gradient(135deg, #F9D332 0%, #FFD700 50%, #F9D332 100%)',
              border: '3px solid #FFA500',
              boxShadow: '0 8px 25px rgba(249, 211, 50, 0.4), inset 0 2px 0 rgba(255, 255, 255, 0.3), inset 0 -2px 0 rgba(0, 0, 0, 0.1)',
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.3)',
              animation: 'pulse-glow 2s ease-in-out infinite alternate'
            }}
          >
            {/* Animated background shimmer */}
            <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-30 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
            
            {/* Left arrows with animation */}
            <div className="flex group-hover:animate-bounce">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:text-red-600 transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="3" fill="none"/>
              </svg>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:text-red-600 transition-colors duration-300 -ml-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M15 19l-7-7 7-7" stroke="currentColor" strokeWidth="3" fill="none"/>
              </svg>
            </div>
            
            <span className="font-extrabold group-hover:tracking-wider transition-all duration-300">Quay lại</span>
            
            {/* Right arrows with animation */}
            <div className="flex group-hover:animate-bounce">
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:text-red-600 transition-colors duration-300" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="3" fill="none"/>
              </svg>
              <svg className="w-4 h-4 sm:w-5 sm:h-5 text-orange-600 group-hover:text-red-600 transition-colors duration-300 -ml-2" fill="currentColor" viewBox="0 0 24 24">
                <path d="M9 5l7 7-7 7" stroke="currentColor" strokeWidth="3" fill="none"/>
              </svg>
            </div>
          </button>
        </div>
      </div>

      {/* Main Video Player */}
      <div className="container mx-auto px-4 lg:py-8 py-4">
        <div className="max-w-7xl mx-auto">
          {/* Video Info Section - Above video */}
          <div className="mb-6 bg-gray-900/80 backdrop-blur-sm rounded-xl p-4 sm:p-6 border border-gray-700">
            <div className="text-center">
              <h1 className="text-lg sm:text-xl md:text-2xl lg:text-3xl font-bold text-white mb-2">
                {video.title}
              </h1>
              <p className="text-sm sm:text-base md:text-lg lg:text-xl text-gray-300">
                {video.description}
              </p>
            </div>
          </div>

          {/* Video Container with Border - Wider on mobile */}
          <div className="relative w-full">
            {/* Glowing Border Effect */}
            <div 
              className="absolute inset-0 rounded-xl sm:rounded-2xl"
              style={{
                background: 'linear-gradient(45deg, #00ff88, #00ff00, #88ff00, #00ff88)',
                padding: '3px',
                borderRadius: '12px'
              }}
            >
              <div className="w-full h-full bg-black rounded-lg sm:rounded-xl"></div>
            </div>
            
            {/* Video Player - Full width, optimized height */}
            <div className="relative z-10 bg-black rounded-lg sm:rounded-xl overflow-hidden w-full mb-2 lg:mb-0" style={{ aspectRatio: isMobile ? '9/16' : '16/9' }}>
              {!video.videoUrl ? (
                // No video URL
                <div className="w-full h-full flex items-center justify-center text-white">
                  <div className="text-center">
                    <div className="text-xl mb-2">❌</div>
                    <div>Video URL không tồn tại</div>
                  </div>
                </div>
              ) : video.videoUrl.includes('vimeo.com') ? (
                // Vimeo video - embed iframe
                <iframe
                  src={`https://player.vimeo.com/video/${video.videoUrl.split('/').pop()}?autoplay=1&muted=0&loop=1&title=0&byline=0&portrait=0`}
                  className="w-full h-full"
                  frameBorder="0"
                  allow="autoplay; fullscreen; picture-in-picture"
                  allowFullScreen
                  title={video.title}
                  onError={(e) => {
                    console.error('Vimeo iframe error:', e);
                    alert('Lỗi tải video Vimeo');
                  }}
                ></iframe>
              ) : (
                // Local video file
                <video 
                  className="w-full h-full object-contain"
                  controls
                  autoPlay
                  muted
                  playsInline
                  onError={(e) => {
                    console.error('Video error:', e);
                    const videoElement = e.target as HTMLVideoElement;
                    console.error('Video error details:', {
                      error: videoElement.error,
                      networkState: videoElement.networkState,
                      readyState: videoElement.readyState,
                      src: videoElement.src,
                      currentSrc: videoElement.currentSrc,
                      videoUrl: video.videoUrl
                    });
                    
                    // Show user-friendly error message
                    alert(`Lỗi phát video: ${videoElement.error?.message || 'Không thể phát video này'}\nURL: ${video.videoUrl}`);
                  }}
                  onPlay={(e) => {
                    // Auto unmute when video starts playing
                    const videoElement = e.target as HTMLVideoElement;
                    videoElement.muted = false;
                  }}
                >
                  <source src={video.videoUrl} type="video/mp4" />
                  <source src={video.videoUrl} type="video/webm" />
                  <source src={video.videoUrl} type="video/ogg" />
                  Your browser does not support the video tag.
                </video>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* Social Media Icons */}
      <SocialMediaIcons />

      {/* Logout Modal */}
      <ModalConfirmLogout 
        isShowLogout={showLogoutModal}
        setIsShowLogout={() => setShowLogoutModal(false)}
      />
    </div>
  );
};

export default VideoPlayer;
